import { createSlice, createAsyncThunk, type ThunkAction, type UnknownAction } from "@reduxjs/toolkit";
import { api } from "@/lib/api";
import { currentStaffSession, staffSessionActions } from "./staffSessionSlice";
import { NO_FEATURES, type Features } from "@/lib/features";
import type { StaffAccessContext } from "@/services/staffAccessContext";

export interface FeaturesState {
  features: Features;
  loaded: boolean;
  loading: boolean;
  requestId?: string | null;
}

const initialState: FeaturesState = {
  features: NO_FEATURES,
  loaded: false,
  loading: false,
};

/**
 * Fetches the feature flags from the backend.
 *
 * Expected API shape:
 *   GET /api/users/me/features
 *   Response: { "amsBooking": true, "amsAvailabilityWeekView": false, "amsHostSiteExclusivity": false }
 *   A flag the response leaves out stays off (NO_FEATURES).
 */
export const fetchFeatures = createAsyncThunk<Features>(
  "features/fetchFeatures",
  async (_, { rejectWithValue }) => {
    try {
      return await api.get<Features>("/api/users/me/features");
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch features"
      );
    }
  }
);

/**
 * Accepts a checked staff access context and, when it opens a new scope (sign-in, another account, or
 * access that changed), reads GET /api/users/me/features once for the flags the access context does
 * not carry: amsAvailabilityWeekView and amsHostSiteExclusivity (AMS M12, decision ams-1ol).
 *
 * They are deliberately not added to the access context: its features feed the scope key's hash, so a
 * new field would change every user's key on deploy, and a changed key revokes the session's scope —
 * open tabs would drop their staff caches and unsaved drafts and remount. The routine 45–60 s
 * re-checks keep the same scope and read nothing more.
 */
export const acceptStaffAccessContext =
  (context: StaffAccessContext): ThunkAction<void, unknown, unknown, UnknownAction> =>
  (dispatch, getState) => {
    const before = currentStaffSession(getState())?.scopeKey ?? null;
    dispatch(staffSessionActions.contextAccepted(context));
    if (before !== context.scopeKey) void dispatch(fetchFeatures());
  };

const featuresSlice = createSlice({
  name: "features",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(staffSessionActions.scopeRevoked, () => initialState)
      .addCase(staffSessionActions.contextAccepted, (state, action) => {
        // The access context carries amsBooking only; the flags fetchFeatures read for this scope stay.
        // An in-flight read is kept too: a revoked scope has already reset requestId.
        state.features = { ...NO_FEATURES, ...state.features, ...action.payload.features };
        state.loaded = true;
      })
      .addCase(fetchFeatures.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
      })
      .addCase(fetchFeatures.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.requestId = null;
        state.features = { ...NO_FEATURES, ...action.payload };
        state.loaded = true;
        state.loading = false;
      })
      .addCase(fetchFeatures.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.requestId = null;
        // Fail closed on the flags only this read reports, but keep amsBooking from the access
        // context, and count it as loaded so ProtectedRoute does not wait or retry forever.
        state.features = { ...NO_FEATURES, amsBooking: state.features.amsBooking };
        state.loaded = true;
        state.loading = false;
      });
  },
});

export default featuresSlice.reducer;
